import React, { useEffect, useMemo, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { makeStyles } from '@material-ui/core/styles';
import Button from '@material-ui/core/Button';
import Chip from '@material-ui/core/Chip';
import Container from '@material-ui/core/Container';
import Grid from '@material-ui/core/Grid';
import Link from '@material-ui/core/Link';
import Paper from '@material-ui/core/Paper';
import Snackbar from '@material-ui/core/Snackbar';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import TextField from '@material-ui/core/TextField';
import Typography from '@material-ui/core/Typography';
import FileCopyIcon from '@material-ui/icons/FileCopy';
import OpenInNewIcon from '@material-ui/icons/OpenInNew';

const { REACT_APP_PROXY_HOST, REACT_APP_WEBSERVER } = process.env;
const JOURNAL_DIAGNOSTIC_FETCH = true;

const journalDiagnosticFetch = (niveau, message, details) => {
  if (!JOURNAL_DIAGNOSTIC_FETCH) {
    return;
  }

  const methode = console[niveau] || console.info;
  methode('[IncidentsCamera] ' + message + ' ' + JSON.stringify(details));
};

const useStyles = makeStyles((theme) => ({
  page: { paddingTop: theme.spacing(3), paddingBottom: theme.spacing(4) },
  paper: { padding: theme.spacing(2), overflowX: 'auto' },
  header: { display: 'flex', justifyContent: 'space-between', gap: theme.spacing(2), flexWrap: 'wrap', alignItems: 'center' },
  filters: { display: 'flex', gap: theme.spacing(2), flexWrap: 'wrap', marginTop: theme.spacing(2), marginBottom: theme.spacing(2) },
  table: { minWidth: 980 },
  summaryTable: { minWidth: 760 },
  selectedRow: { backgroundColor: '#eef5ff' },
  sequence: { marginTop: theme.spacing(2) },
  eventCard: { borderLeft: '4px solid #0066ff', padding: theme.spacing(1.5), marginBottom: theme.spacing(1), backgroundColor: '#fafafa' },
  eventMeta: { display: 'flex', gap: theme.spacing(1), flexWrap: 'wrap', marginBottom: theme.spacing(1) },
  pre: { whiteSpace: 'pre-wrap', wordBreak: 'break-word', fontSize: 12, backgroundColor: '#111827', color: '#f9fafb', padding: theme.spacing(1.5), borderRadius: 4 },
  muted: { color: '#666' },
  degraded: { borderLeft: '4px solid #f59e0b', padding: theme.spacing(1.5), marginBottom: theme.spacing(2), backgroundColor: '#fff7ed' },
  nowrap: { whiteSpace: 'nowrap' },
  stable: { backgroundColor: '#ecfdf5', color: '#065f46' },
  warning: { backgroundColor: '#fff7ed', color: '#9a3412' },
  critical: { backgroundColor: '#fef2f2', color: '#991b1b' },
}));

const parseJsonMessage = (message) => {
  if (!message || typeof message !== 'string') {
    return {};
  }

  const trimmed = message.trim();
  if (!trimmed || (trimmed[0] !== '{' && trimmed[0] !== '[')) {
    return {};
  }

  try {
    const parsed = JSON.parse(trimmed);
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : {};
  } catch (erreurJson) {
    return {};
  }
};

const parsePayload = (payload) => {
  if (!payload) {
    return {};
  }
  if (typeof payload === 'object' && !Array.isArray(payload)) {
    return payload;
  }
  return parseJsonMessage(payload);
};

const lireChamp = (source, champs, valeurDefaut) => {
  for (let i = 0; i < champs.length; i += 1) {
    const champ = champs[i];
    if (source && source[champ] !== undefined && source[champ] !== null && source[champ] !== '') {
      return source[champ];
    }
  }
  return valeurDefaut;
};

const normaliserDate = (valeur) => {
  if (!valeur) {
    return null;
  }

  if (typeof valeur === 'number') {
    return valeur;
  }

  const texte = String(valeur);
  if (/^\d+$/.test(texte)) {
    return parseInt(texte, 10);
  }

  const date = Date.parse(texte);
  return Number.isNaN(date) ? null : date;
};

const formaterDate = (timestamp) => {
  if (!timestamp) {
    return 'Date inconnue';
  }
  return new Date(timestamp).toLocaleString();
};

const texte = (valeur) => {
  if (valeur === undefined || valeur === null || valeur === '') {
    return 'n/d';
  }
  if (Array.isArray(valeur)) {
    return valeur.join(', ');
  }
  if (typeof valeur === 'object') {
    return JSON.stringify(valeur);
  }
  return String(valeur);
};

const extraireCause = (json, erreur) => {
  const cause = lireChamp(json, ['cause', 'rootCause', 'reason', 'categorie', 'category'], '');
  if (cause) {
    return texte(cause);
  }

  const causes = lireChamp(json, ['causes', 'exceptions', 'innerCauses'], '');
  if (causes) {
    return texte(causes);
  }

  const message = lireChamp(erreur, ['message', 'Message'], '');
  return message ? String(message).slice(0, 140) : 'n/d';
};

const estIncidentCamera = (incident) => {
  const pile = [
    incident.typeAppareil,
    incident.typeEvenement,
    incident.message,
    incident.cause,
    incident.raw && incident.raw.type,
    incident.raw && incident.raw.source,
  ].join(' ').toLowerCase();

  return Boolean(
    incident.correlationId ||
    incident.sessionId ||
    incident.recoverable !== 'n/d' ||
    pile.indexOf('camera') >= 0 ||
    pile.indexOf('caméra') >= 0 ||
    pile.indexOf('synccam') >= 0 ||
    pile.indexOf('capture') >= 0 ||
    pile.indexOf('runtime') >= 0 ||
    pile.indexOf('lifecycle') >= 0
  );
};

const normaliserErreur = (erreur, index) => {
  const json = { ...parsePayload(erreur.payload), ...parseJsonMessage(erreur.message) };
  const timestamp = normaliserDate(lireChamp(json, ['timestamp', 'date', 'time', 'occurredAt'], lireChamp(erreur, ['date', 'timestamp', 'createdAt'], null)));
  const correlationId = lireChamp(json, ['correlationId', 'correlationID', 'correlation_id'], lireChamp(erreur, ['correlationId', 'correlationID', 'correlation_id'], ''));
  const sessionId = lireChamp(json, ['sessionId', 'sessionID', 'session_id'], lireChamp(erreur, ['sessionId', 'sessionID', 'session_id'], ''));
  const appareil = lireChamp(json, ['telId', 'telephoneId', 'appareilId', 'appareilid', 'deviceId'], lireChamp(erreur, ['telId', 'appareilid', 'appareilId'], 'n/d'));
  const versionApp = lireChamp(json, ['version', 'appVersion', 'versionApp', 'syncCamVersion'], lireChamp(erreur, ['version', 'appVersion'], 'n/d'));
  const severity = lireChamp(json, ['severity', 'level', 'niveau'], lireChamp(erreur, ['niveau', 'severity', 'level'], 'n/d'));
  const typeEvenement = lireChamp(json, ['evenementType', 'eventType', 'typeEvenement', 'event', 'type', 'name'], lireChamp(erreur, ['evenement_type', 'type', 'typeappareil'], 'Erreur runtime'));
  const recoverable = lireChamp(json, ['recoverable', 'isRecoverable'], lireChamp(erreur, ['recoverable'], 'n/d'));
  const etatCamera = lireChamp(json, ['cameraState', 'etatCamera', 'cameraLifecycle', 'lifecycle'], '');
  const etatRuntime = lireChamp(json, ['runtimeState', 'etatRuntime', 'runtime'], '');
  const categorieOperationnelle = lireChamp(json, ['categorieOperationnelle', 'categorie_operationnelle'], '');
  const severiteOperationnelle = lireChamp(json, ['severiteOperationnelle', 'severite_operationnelle'], '');

  return {
    id: lireChamp(erreur, ['erreurid', 'id', '_id'], 'incident-' + index),
    timestamp,
    appareil: texte(appareil),
    typeAppareil: lireChamp(erreur, ['typeappareil', 'typeAppareil'], 'n/d'),
    versionApp: texte(versionApp),
    severity: texte(severity),
    typeEvenement: texte(typeEvenement),
    categorieOperationnelle: texte(categorieOperationnelle) === 'n/d' ? '' : texte(categorieOperationnelle),
    severiteOperationnelle: texte(severiteOperationnelle) === 'n/d' ? '' : texte(severiteOperationnelle),
    cause: extraireCause(json, erreur),
    recoverable: texte(recoverable),
    correlationId: texte(correlationId) === 'n/d' ? '' : texte(correlationId),
    sessionId: texte(sessionId) === 'n/d' ? '' : texte(sessionId),
    etatCamera: texte(etatCamera) === 'n/d' ? '' : texte(etatCamera),
    etatRuntime: texte(etatRuntime) === 'n/d' ? '' : texte(etatRuntime),
    message: lireChamp(erreur, ['message', 'Message'], ''),
    json,
    raw: erreur,
  };
};

const cleRegroupement = (incident) => (
  incident.correlationId ||
  incident.sessionId ||
  incident.appareil + '-' + incident.typeEvenement + '-' + incident.timestamp
);

const construireIncidents = (evenements) => {
  const groupes = {};
  evenements.forEach((evenement) => {
    const cle = cleRegroupement(evenement);
    groupes[cle] = groupes[cle] || [];
    groupes[cle].push(evenement);
  });

  return Object.keys(groupes).map((cle) => {
    const sequence = groupes[cle].sort((a, b) => (b.timestamp || 0) - (a.timestamp || 0));
    const principal = sequence[0];
    return { ...principal, cle, nbEvenements: sequence.length, sequence };
  }).sort((a, b) => (b.timestamp || 0) - (a.timestamp || 0));
};

const jourCaptation = (incident) => {
  if (!incident.timestamp) {
    return 'Journee inconnue';
  }
  return new Date(incident.timestamp).toLocaleDateString('fr-CA');
};

const scoreEvenement = (incident) => {
  const type = String(incident.typeEvenement || '').toUpperCase();
  const categorie = String(incident.categorieOperationnelle || '').toUpperCase();
  const severity = String(incident.severity || '').toUpperCase();

  if (
    categorie === 'INCIDENT' ||
    severity === 'ERROR' ||
    severity === 'CRITIQUE' ||
    type === 'VIDEO_PERDUE' ||
    type === 'CAMERA_RECORDING_LOST' ||
    type === 'CAMERA_FINALIZE_ERREUR' ||
    type === 'CAMERA_FINALIZE_ERROR'
  ) {
    return 'critique';
  }
  if (categorie === 'WARN' || severity === 'WARN' || type === 'VIDEO_INCIDENT') {
    return 'warn';
  }
  return 'info';
};

const stabiliteJournee = (compteurs) => {
  if (compteurs.critiques >= 3 || compteurs.videoIncident >= 10 || compteurs.videoPerdue > 0 || compteurs.recordingLost > 0) {
    return 'critique';
  }
  if (compteurs.critiques >= 1 || compteurs.videoIncident >= 3 || compteurs.warn >= 5) {
    return 'degrade';
  }
  return 'stable';
};

const construireJourneesCamera = (evenements) => {
  const groupes = {};
  evenements.forEach((incident) => {
    const appareil = incident.appareil || 'n/d';
    const journee = jourCaptation(incident);
    const cle = appareil + '|' + journee;
    const type = String(incident.typeEvenement || '').toUpperCase();
    const score = scoreEvenement(incident);

    groupes[cle] = groupes[cle] || {
      cle,
      appareil,
      journee,
      total: 0,
      critiques: 0,
      warn: 0,
      videoIncident: 0,
      videoPerdue: 0,
      finalizeErreur: 0,
      recordingLost: 0,
      dernierTimestamp: 0,
    };

    groupes[cle].total += 1;
    groupes[cle].dernierTimestamp = Math.max(groupes[cle].dernierTimestamp, incident.timestamp || 0);
    if (score === 'critique') {
      groupes[cle].critiques += 1;
    }
    if (score === 'warn') {
      groupes[cle].warn += 1;
    }
    if (type === 'VIDEO_INCIDENT') {
      groupes[cle].videoIncident += 1;
    }
    if (type === 'VIDEO_PERDUE') {
      groupes[cle].videoPerdue += 1;
    }
    if (type === 'CAMERA_FINALIZE_ERREUR' || type === 'CAMERA_FINALIZE_ERROR') {
      groupes[cle].finalizeErreur += 1;
    }
    if (type === 'CAMERA_RECORDING_LOST') {
      groupes[cle].recordingLost += 1;
    }
  });

  return Object.keys(groupes).map((cle) => {
    const groupe = groupes[cle];
    return { ...groupe, stabilite: stabiliteJournee(groupe) };
  }).sort((a, b) => (b.journee.localeCompare(a.journee) || String(a.appareil).localeCompare(String(b.appareil))));
};

const sequenceLiee = (incident, evenements) => {
  if (!incident) {
    return [];
  }

  const fenetreMs = 30 * 60 * 1000;
  return evenements.filter((evenement) => {
    if (incident.correlationId && evenement.correlationId === incident.correlationId) {
      return true;
    }
    if (incident.sessionId && evenement.sessionId === incident.sessionId) {
      return true;
    }
    if (incident.appareil !== 'n/d' && evenement.appareil === incident.appareil && incident.timestamp && evenement.timestamp) {
      return Math.abs(evenement.timestamp - incident.timestamp) <= fenetreMs;
    }
    return evenement.id === incident.id;
  }).sort((a, b) => (a.timestamp || 0) - (b.timestamp || 0));
};

const construireContexteCopiable = (incident, sequence) => {
  const lignes = [
    '# Incident Camera SyncStats',
    '',
    '## Resume',
    '- Timestamp: ' + formaterDate(incident.timestamp),
    '- Appareil: ' + incident.appareil,
    '- Version app: ' + incident.versionApp,
    '- Severity: ' + incident.severity,
    '- Type evenement: ' + incident.typeEvenement,
    '- Cause: ' + incident.cause,
    '- Recoverable: ' + incident.recoverable,
    '- CorrelationId: ' + (incident.correlationId || 'n/d'),
    '- SessionId: ' + (incident.sessionId || 'n/d'),
    '- Etat camera: ' + (incident.etatCamera || 'n/d'),
    '- Etat runtime: ' + (incident.etatRuntime || 'n/d'),
    '',
    '## Sequence chronologique liee',
  ];

  sequence.forEach((evenement, index) => {
    lignes.push(
      '',
      '### Evenement ' + (index + 1),
      '- Timestamp: ' + formaterDate(evenement.timestamp),
      '- Appareil: ' + evenement.appareil,
      '- Severity: ' + evenement.severity,
      '- Type: ' + evenement.typeEvenement,
      '- Cause: ' + evenement.cause,
      '- Recoverable: ' + evenement.recoverable,
      '- CorrelationId: ' + (evenement.correlationId || 'n/d'),
      '- SessionId: ' + (evenement.sessionId || 'n/d'),
      '- Etat camera: ' + (evenement.etatCamera || 'n/d'),
      '- Etat runtime: ' + (evenement.etatRuntime || 'n/d'),
      '- Message: ' + texte(evenement.message)
    );

    if (Object.keys(evenement.json || {}).length > 0) {
      lignes.push('- Payload JSON extrait:');
      lignes.push('```json');
      lignes.push(JSON.stringify(evenement.json, null, 2));
      lignes.push('```');
    }
  });

  lignes.push('', '## Payload brut normalise');
  lignes.push('```json');
  lignes.push(JSON.stringify({ incident, sequence }, null, 2));
  lignes.push('```');

  return lignes.join('\n');
};

const IncidentsCamera = () => {
  const classes = useStyles();
  const [evenements, setEvenements] = useState([]);
  const [selection, setSelection] = useState(null);
  const [chargement, setChargement] = useState(true);
  const [erreur, setErreur] = useState('');
  const [etatDegrade, setEtatDegrade] = useState(null);
  const [filtre, setFiltre] = useState('');
  const [messageCopie, setMessageCopie] = useState('');

  useEffect(() => {
    const urlLogsErreurs = (REACT_APP_PROXY_HOST || '') + '/log/erreurs';
    const fenetre = typeof window !== 'undefined' ? window : null;
    const urlResolue = fenetre ? new URL(urlLogsErreurs, fenetre.location.href).href : urlLogsErreurs;
    const risqueMixedContent = Boolean(
      fenetre &&
      fenetre.location.protocol === 'https:' &&
      urlResolue.indexOf('http://') === 0
    );

    journalDiagnosticFetch('info', 'diagnostic runtime', {
      REACT_APP_PROXY_HOST,
      REACT_APP_WEBSERVER,
      urlDemandee: urlLogsErreurs,
      urlResolue,
      protocolePage: fenetre ? fenetre.location.protocol : 'n/d',
      protocoleApi: urlResolue.split(':')[0] + ':',
      risqueMixedContent,
    });
    journalDiagnosticFetch('info', 'debut fetch /log/erreurs', { urlResolue });

    fetch(urlLogsErreurs, { method: 'GET' })
      .then((res) => {
        journalDiagnosticFetch('info', 'reponse fetch /log/erreurs', {
          ok: res.ok,
          status: res.status,
          statusText: res.statusText,
          url: res.url,
          type: res.type,
        });

        return res.text().then((corps) => {
          if (!res.ok) {
            const erreurHttp = new Error('HTTP ' + res.status + ' ' + res.statusText);
            erreurHttp.status = res.status;
            erreurHttp.statusText = res.statusText;
            erreurHttp.url = res.url || urlResolue;
            erreurHttp.corps = corps;
            throw erreurHttp;
          }

          try {
            return corps ? JSON.parse(corps) : [];
          } catch (erreurJson) {
            erreurJson.corps = corps;
            throw erreurJson;
          }
        });
      })
      .then((payload) => {
        const estDegrade = Boolean(payload && payload.degraded);
        const erreurs = Array.isArray(payload)
          ? payload
          : (payload && Array.isArray(payload.erreurs) ? payload.erreurs : []);

        journalDiagnosticFetch('info', 'succes fetch /log/erreurs', {
          typePayload: Array.isArray(payload) ? 'array' : typeof payload,
          degraded: estDegrade,
          source: payload && payload.source,
          nombreErreurs: erreurs.length,
        });

        setEtatDegrade(estDegrade ? {
          source: payload.source || 'postgres',
          message: payload.message || 'Lecture des erreurs temporairement indisponible',
        } : null);
        setErreur('');
        const normalises = (erreurs || []).map(normaliserErreur).filter(estIncidentCamera);
        setEvenements(normalises);
        const incidents = construireIncidents(normalises);
        setSelection(incidents.length > 0 ? incidents[0] : null);
        setChargement(false);
      })
      .catch((erreurFetch) => {
        journalDiagnosticFetch('error', 'erreur fetch /log/erreurs', {
          message: erreurFetch && erreurFetch.message,
          name: erreurFetch && erreurFetch.name,
          stack: erreurFetch && erreurFetch.stack,
          status: erreurFetch && erreurFetch.status,
          statusText: erreurFetch && erreurFetch.statusText,
          url: (erreurFetch && erreurFetch.url) || urlResolue,
          contenuErreur: erreurFetch && erreurFetch.corps ? String(erreurFetch.corps).slice(0, 1000) : '',
          risqueMixedContent,
        });

        setEtatDegrade(null);
        setErreur('Impossible de charger /log/erreurs.');
        setChargement(false);
      });
  }, []);

  const incidents = useMemo(() => construireIncidents(evenements), [evenements]);
  const journeesCamera = useMemo(() => construireJourneesCamera(evenements), [evenements]);
  const incidentsFiltres = useMemo(() => {
    if (!filtre) {
      return incidents;
    }
    const recherche = filtre.toLowerCase();
    return incidents.filter((incident) => [
      incident.appareil,
      incident.versionApp,
      incident.severity,
      incident.typeEvenement,
      incident.cause,
      incident.correlationId,
      incident.sessionId,
    ].join(' ').toLowerCase().indexOf(recherche) >= 0);
  }, [filtre, incidents]);
  const journeesCameraFiltrees = useMemo(() => {
    if (!filtre) {
      return journeesCamera;
    }
    const recherche = filtre.toLowerCase();
    return journeesCamera.filter((journee) => [
      journee.appareil,
      journee.journee,
      journee.stabilite,
    ].join(' ').toLowerCase().indexOf(recherche) >= 0);
  }, [filtre, journeesCamera]);
  const sequence = useMemo(() => sequenceLiee(selection, evenements), [selection, evenements]);

  const classeStabilite = (stabilite) => {
    if (stabilite === 'critique') {
      return classes.critical;
    }
    if (stabilite === 'degrade') {
      return classes.warning;
    }
    return classes.stable;
  };

  const copierIncident = async () => {
    if (!selection) {
      return;
    }
    const texteCopiable = construireContexteCopiable(selection, sequence);
    try {
      await navigator.clipboard.writeText(texteCopiable);
      setMessageCopie('Incident complet copie.');
    } catch (erreurCopie) {
      setMessageCopie('Copie automatique indisponible. Le contexte reste visible dans le detail.');
    }
  };

  return (
    <Container maxWidth={false} className={classes.page}>
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Paper className={classes.paper}>
            <div className={classes.header}>
              <div>
                <Typography variant="h4" color="secondary">Incidents Camera</Typography>
                <Typography variant="body2" className={classes.muted}>
                  Surface operationnelle V1 basee sur /log/erreurs et les champs riches SyncCam quand ils sont presents.
                </Typography>
              </div>
              <Button
                variant="contained"
                color="primary"
                startIcon={<FileCopyIcon />}
                disabled={!selection}
                onClick={copierIncident}
              >
                Copier incident complet
              </Button>
            </div>

            <div className={classes.filters}>
              <TextField
                label="Filtrer incidents"
                variant="outlined"
                size="small"
                value={filtre}
                onChange={(event) => setFiltre(event.target.value)}
                style={{ minWidth: 280 }}
              />
              <Button variant="outlined" onClick={() => setFiltre('')}>Reinitialiser</Button>
              <Button component={RouterLink} to="/Operations/Monitorer" variant="outlined">
                Voir erreurs runtime
              </Button>
            </div>

            {chargement && <Typography>Chargement des incidents...</Typography>}
            {!chargement && etatDegrade && (
              <div className={classes.degraded}>
                <Typography variant="subtitle1">Mode degrade incidents camera</Typography>
                <Typography variant="body2">
                  {etatDegrade.message}. Source: {etatDegrade.source}. Le cockpit reste disponible, mais les erreurs historiques sont temporairement vides.
                </Typography>
              </div>
            )}
            {erreur && <Typography color="error">{erreur}</Typography>}
            {!chargement && !erreur && !etatDegrade && incidents.length === 0 && (
              <Typography>Aucun incident camera detecte dans /log/erreurs.</Typography>
            )}

            {!chargement && journeesCamera.length > 0 && (
              <React.Fragment>
                <Typography variant="h6">Vue operationnelle par camera et journee</Typography>
                <Typography variant="body2" className={classes.muted}>
                  La journee est calculee depuis le timestamp de l'evenement SyncCam. Les details forensic restent disponibles plus bas.
                </Typography>
                <Table className={classes.summaryTable} size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Camera</TableCell>
                      <TableCell>Journee</TableCell>
                      <TableCell>Stabilite</TableCell>
                      <TableCell align="right">VIDEO_INCIDENT</TableCell>
                      <TableCell align="right">VIDEO_PERDUE</TableCell>
                      <TableCell align="right">CAMERA_FINALIZE_ERREUR</TableCell>
                      <TableCell align="right">CAMERA_RECORDING_LOST</TableCell>
                      <TableCell align="right">Critiques</TableCell>
                      <TableCell>Dernier evenement</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {journeesCameraFiltrees.map((journee) => (
                      <TableRow key={journee.cle}>
                        <TableCell>{journee.appareil}</TableCell>
                        <TableCell>{journee.journee}</TableCell>
                        <TableCell>
                          <Chip size="small" className={classeStabilite(journee.stabilite)} label={journee.stabilite} />
                        </TableCell>
                        <TableCell align="right">{journee.videoIncident}</TableCell>
                        <TableCell align="right">{journee.videoPerdue}</TableCell>
                        <TableCell align="right">{journee.finalizeErreur}</TableCell>
                        <TableCell align="right">{journee.recordingLost}</TableCell>
                        <TableCell align="right">{journee.critiques}</TableCell>
                        <TableCell className={classes.nowrap}>{formaterDate(journee.dernierTimestamp)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </React.Fragment>
            )}

            {!chargement && incidents.length > 0 && (
              <React.Fragment>
                <Typography variant="h6" style={{ marginTop: 24 }}>Details forensic</Typography>
                <Table className={classes.table} size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Timestamp</TableCell>
                      <TableCell>Appareil</TableCell>
                      <TableCell>Version</TableCell>
                      <TableCell>Severity</TableCell>
                      <TableCell>Type evenement</TableCell>
                      <TableCell>Cause</TableCell>
                      <TableCell>Recoverable</TableCell>
                      <TableCell>Correlation / Session</TableCell>
                      <TableCell>Sequence</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {incidentsFiltres.map((incident) => (
                      <TableRow
                        hover
                        key={incident.cle}
                        onClick={() => setSelection(incident)}
                        className={selection && selection.cle === incident.cle ? classes.selectedRow : ''}
                      >
                        <TableCell className={classes.nowrap}>{formaterDate(incident.timestamp)}</TableCell>
                        <TableCell>{incident.appareil}</TableCell>
                        <TableCell>{incident.versionApp}</TableCell>
                        <TableCell>{incident.severity}</TableCell>
                        <TableCell>{incident.typeEvenement}</TableCell>
                        <TableCell>{incident.cause}</TableCell>
                        <TableCell>{incident.recoverable}</TableCell>
                        <TableCell>
                          <div>{incident.correlationId || 'n/d'}</div>
                          <div className={classes.muted}>{incident.sessionId || 'n/d'}</div>
                        </TableCell>
                        <TableCell>{incident.nbEvenements}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </React.Fragment>
            )}
          </Paper>
        </Grid>

        {selection && (
          <Grid item xs={12}>
            <Paper className={classes.paper}>
              <div className={classes.header}>
                <div>
                  <Typography variant="h5">Sequence liee</Typography>
                  <Typography variant="body2" className={classes.muted}>
                    Regroupement par correlationId/sessionId, sinon par appareil dans une fenetre de 30 minutes.
                  </Typography>
                </div>
                {selection.appareil !== 'n/d' && REACT_APP_WEBSERVER && (
                  <Link href={REACT_APP_WEBSERVER + '/monitoring/' + selection.appareil} target="_blank" rel="noopener noreferrer">
                    Logs bruts appareil <OpenInNewIcon fontSize="small" />
                  </Link>
                )}
              </div>

              <div className={classes.sequence}>
                {sequence.map((evenement, index) => (
                  <div className={classes.eventCard} key={evenement.id + '-' + index}>
                    <div className={classes.eventMeta}>
                      <Chip size="small" label={formaterDate(evenement.timestamp)} />
                      <Chip size="small" label={'Appareil ' + evenement.appareil} />
                      <Chip size="small" label={evenement.severity} />
                      <Chip size="small" label={evenement.typeEvenement} />
                      {evenement.recoverable !== 'n/d' && <Chip size="small" label={'Recoverable ' + evenement.recoverable} />}
                    </div>
                    <Typography variant="body2"><strong>Cause:</strong> {evenement.cause}</Typography>
                    <Typography variant="body2"><strong>Camera:</strong> {evenement.etatCamera || 'n/d'} | <strong>Runtime:</strong> {evenement.etatRuntime || 'n/d'}</Typography>
                    <Typography variant="body2"><strong>Correlation:</strong> {evenement.correlationId || 'n/d'} | <strong>Session:</strong> {evenement.sessionId || 'n/d'}</Typography>
                    <pre className={classes.pre}>{texte(evenement.message)}</pre>
                  </div>
                ))}
              </div>
            </Paper>
          </Grid>
        )}
      </Grid>
      <Snackbar open={Boolean(messageCopie)} autoHideDuration={3500} onClose={() => setMessageCopie('')} message={messageCopie} />
    </Container>
  );
};

export default IncidentsCamera;



// WEBPACK FOOTER //
// src/components/incidentsCamera/IncidentsCamera.js